/**
 * TW Service — Word of God flyer entries (public, no CMS auth required).
 * Collection: twEntries / Document ID: YYYY-MM-DD
 */

import {
  doc, getDoc, setDoc,
  collection, getDocs, query, where, orderBy,
  serverTimestamp, Timestamp,
} from 'firebase/firestore';
import {
  ref, uploadBytes, getDownloadURL, deleteObject,
} from 'firebase/storage';
import { db, storage } from '@/config/firebase';

export interface TWEntry {
  date:           string; // YYYY-MM-DD
  englishQuote:   string;
  malayalamQuote: string;
  hindiQuote:     string;
  reference:      string; // e.g. "Habakkuk 3:17"
  flyerUrl:       string | null;
  flyerPath:      string | null;
  updatedAt:      Date | null;
}

function toDate(v: unknown): Date | null {
  if (!v) return null;
  if (v instanceof Timestamp) return v.toDate();
  if (v instanceof Date) return v;
  return null;
}

// Valid passcodes — any one of these unlocks editing
export const TW_PASSCODES = ['984708', '952686', '047836', '808802'];
export const TW_SESSION_KEY = 'tw_edit_unlocked';

export function checkTWPasscode(code: string): boolean {
  return TW_PASSCODES.includes(code.trim());
}

// Format date as YYYY-MM-DD
export function dateToId(date: Date): string {
  return date.toISOString().split('T')[0];
}

// Today's date string in local time
export function todayId(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export async function getTWEntry(dateId: string): Promise<TWEntry | null> {
  const snap = await getDoc(doc(db, 'twEntries', dateId));
  if (!snap.exists()) return null;
  const data = snap.data() as Record<string, unknown>;
  return {
    date:           dateId,
    englishQuote:   (data.englishQuote   as string) ?? '',
    malayalamQuote: (data.malayalamQuote as string) ?? '',
    hindiQuote:     (data.hindiQuote     as string) ?? '',
    reference:      (data.reference      as string) ?? '',
    flyerUrl:       (data.flyerUrl       as string | null) ?? null,
    flyerPath:      (data.flyerPath      as string | null) ?? null,
    updatedAt:      toDate(data.updatedAt),
  };
}

export async function saveTWEntry(
  dateId: string,
  fields: Omit<TWEntry, 'date' | 'updatedAt'>,
): Promise<void> {
  await setDoc(doc(db, 'twEntries', dateId), {
    ...fields,
    date:      dateId,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

// Get all dates in a month that have entries
export async function getTWEntryDatesInMonth(year: number, month: number): Promise<Set<string>> {
  const startId = `${year}-${String(month).padStart(2, '0')}-01`;
  const endId   = `${year}-${String(month).padStart(2, '0')}-31`;
  const snap = await getDocs(
    query(
      collection(db, 'twEntries'),
      where('date', '>=', startId),
      where('date', '<=', endId),
    )
  );
  return new Set(snap.docs.map(d => d.id));
}

// Upload flyer image; returns { url, path }
export async function uploadTWFlyer(
  dateId: string, file: File,
): Promise<{ url: string; path: string }> {
  const ext  = file.name.split('.').pop() ?? 'jpg';
  const path = `tw-flyers/${dateId}.${ext}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file, { contentType: file.type });
  const url = await getDownloadURL(storageRef);
  return { url, path };
}

export async function deleteTWFlyer(path: string): Promise<void> {
  try {
    await deleteObject(ref(storage, path));
  } catch {
    // ignore if already deleted
  }
}

// Build the combined copy text (blank line between each section)
export function buildCopyText(entry: TWEntry): string {
  const parts: string[] = [];
  if (entry.englishQuote.trim())   parts.push(entry.englishQuote.trim());
  if (entry.malayalamQuote.trim()) parts.push(entry.malayalamQuote.trim());
  if (entry.hindiQuote.trim())     parts.push(entry.hindiQuote.trim());
  if (entry.reference.trim())      parts.push(entry.reference.trim());
  return parts.join('\n\n');
}

// Get all entries that have a flyer, up to today, sorted newest first
export async function getTWEntriesWithFlyers(): Promise<TWEntry[]> {
  const today = todayId();
  const snap = await getDocs(
    query(
      collection(db, 'twEntries'),
      where('date', '<=', today),
      orderBy('date', 'desc'),
    )
  );
  return snap.docs
    .map(d => {
      const data = d.data() as Record<string, unknown>;
      return {
        date:           d.id,
        englishQuote:   (data.englishQuote   as string) ?? '',
        malayalamQuote: (data.malayalamQuote as string) ?? '',
        hindiQuote:     (data.hindiQuote     as string) ?? '',
        reference:      (data.reference      as string) ?? '',
        flyerUrl:       (data.flyerUrl       as string | null) ?? null,
        flyerPath:      (data.flyerPath      as string | null) ?? null,
        updatedAt:      toDate(data.updatedAt),
      };
    })
    .filter(e => !!e.flyerUrl);
}
